import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Spin, Row, Col, Typography, App } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";

import { getInstitutionApi } from "../../../../../apiservice/setting";
import { purchaseReturnWithDetailsApi } from "../../../../../apiservice/purchase";
import ReportDom from "../../../../../components/common/ReportDom";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import PurchaseReturnVoucherBody from "../../PurchaseReturnVoucher/components/PurchaseReturnVoucherBody";
import PurchaseReturnVoucherBodyDesignA from "../../PurchaseReturnVoucher/components/PurchaseReturnVoucherBodyDesignA";
import { useShareDocument } from "../../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const PurchaseReturnQuickVoucher = () => {
  const { id } = useParams(); // route: /purchase-return/quick/:id
  const navigate = useNavigate();
  const { message } = App.useApp();
  const authInfo = useSelector(selectAuthInfo);

  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Purchase_Return_Voucher_${voucher?.pur_r_voucher_no || "Voucher"}`,
  });

  // 🔹 Fetch institution + voucher details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [instRes, voucherRes] = await Promise.all([
          getInstitutionApi(),
          purchaseReturnWithDetailsApi({ pur_r_id: id, from: "voucher" }),
        ]);

        setInstitution(instRes?.data || null);

        if (voucherRes?.data?.length > 0) {
          setVoucher(voucherRes.data[0]);
        } else {
          message.warning("No purchase return voucher found.");
        }
      } catch (err) {
        console.error("Failed to load Purchase Return Quick Voucher:", err);
        message.error("Failed to load purchase return voucher.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id, message]);

  useEffect(() => {
    if (shareOpen && supplierEmail) {
      setEmail(supplierEmail);
    }
  }, [shareOpen, supplierEmail, setEmail]);

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/purchase-return")}>Back</Button>
      </div>
    );

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Bar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
          <Row gutter={8}>
            <Col>
              <ShareDocumentButton
                disabled={!voucher}
                shareOpen={shareOpen}
                setShareOpen={setShareOpen}
                email={email}
                setEmail={setEmail}
                loading={loading}
                onEmail={() =>
                  handleEmailShare({
                    url: `/print/purchase-return/${voucher.pur_r_id}`,
                    fileName: `PurchaseReturn_${
                      voucherNo ||
                      voucher.pur_r_voucher_no ||
                      "Voucher"
                    }.pdf`,
                    name: accName || "",
                    docNo:
                      voucherNo ||
                      voucher.pur_r_voucher_no ||
                      "Voucher",
                  })
                }
                onWhatsApp={() =>
                  handleWhatsAppShare({
                    url: `/print/purchase-return/${voucher.pur_r_id}`,
                    fileName: `PurchaseReturn_${
                      voucherNo ||
                      voucher.pur_r_voucher_no ||
                      "Voucher"
                    }.pdf`,
                    text: `Hello ${accName || ""}, Your Purchase Return ${
                      voucherNo ||
                      voucher.pur_r_voucher_no ||
                      "Voucher"
                    } is ready. Download here:`,
                  })
                }
              />
            </Col>

            <Col>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              >
                Print Voucher
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Voucher Body */}
      <Card className="shadow-md rounded-xl" style={{ padding: 24, background: "white" }}>
        <PurchaseReturnVoucherBody
          pur_r_id={voucher.pur_r_id}
          institution={institution}
          printMode={false}
          setVoucherNo={setVoucherNo}
          setAccName={setAccName}
          setSupplierEmail={setSupplierEmail}
        />
      </Card>

      {/* Hidden Printable DOM */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          mode={institution?.print_template === "design_a" ? "no-title" : undefined}
          title="Purchase Return Voucher"
          filters={{
            fromDate: voucher.created_date,
            toDate: voucher.created_date,
          }}
          renderTable={() =>
            institution?.print_template === "design_a" ? (
              <PurchaseReturnVoucherBodyDesignA pur_r_id={voucher.pur_r_id} />
            ) : (
              <PurchaseReturnVoucherBody
                pur_r_id={voucher.pur_r_id}
                institution={institution}
                printMode
              />
            )
          }
        />
      </div>
    </div>
  );
};

export default PurchaseReturnQuickVoucher;
